/**
 * Backend status — global, no per-page.
 *
 * Detecta cuando el backend está caído o el usuario perdió internet y
 * expone un status global que el `<BackendStatusBanner>` (banner top fijo)
 * y el `<EmptyState>` (que cambia a `<ConnectionErrorState>` cuando
 * aplica) consumen.
 *
 * Estados:
 *   - 'ok'             → backend respondiendo normal
 *   - 'network-error'  → sin internet (`navigator.onLine === false`) o
 *                        N errores de transporte consecutivos
 *   - 'server-error'   → N errores 5xx consecutivos
 *
 * Lógica de threshold:
 *   - `navigator.onLine === false` → 'network-error' inmediato.
 *   - Errores de fetch del apiClient: si llegan ERROR_THRESHOLD dentro de
 *     ERROR_WINDOW_MS, escalamos a status 'network-error'/'server-error'.
 *     Un solo error aislado NO levanta el banner — eso evita falsos
 *     positivos por requests que aborten o timeouts puntuales.
 *   - Cualquier respuesta 2xx posterior baja el banner automáticamente
 *     (resetea el contador y vuelve a 'ok').
 *   - El botón "Reintentar" del banner hace `GET /health` y, si responde
 *     200, baja el banner explícitamente.
 *
 * Diseño deliberado: el contexto NO conoce de las páginas. Las páginas
 * tampoco lo conocen — solo el `<BackendStatusBanner>` global y el
 * `<EmptyState>` (vía `useBackendStatus`). Cero invasivo.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { apiClient, type FetchErrorKind } from '@/services/api.service';

export type BackendStatus = 'ok' | 'network-error' | 'server-error';

interface BackendStatusContextValue {
  status: BackendStatus;
  /** Reintenta la conexión: ping a /health, si OK baja el banner. */
  retry: () => Promise<void>;
  /** `true` mientras `retry()` está corriendo. */
  isRetrying: boolean;
}

const BackendStatusContext = createContext<BackendStatusContextValue | null>(
  null,
);

/**
 * Cantidad de errores consecutivos del apiClient que necesitamos antes
 * de levantar el banner. 1 error puede ser ruido (timeout puntual,
 * petición abortada en una page change). 2 dentro de la ventana sí es
 * patrón de outage.
 */
const ERROR_THRESHOLD = 2;

/**
 * Ventana en la que se cuentan errores consecutivos. Si pasan más de
 * ERROR_WINDOW_MS sin error, el contador se resetea — un error de hace
 * 5 minutos no se debe combinar con uno de ahora.
 */
const ERROR_WINDOW_MS = 30_000;

interface ErrorTrack {
  count: number;
  firstAt: number;
  lastKind: FetchErrorKind | null;
}

const EMPTY_TRACK: ErrorTrack = { count: 0, firstAt: 0, lastKind: null };

export function BackendStatusProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<BackendStatus>(() =>
    typeof navigator !== 'undefined' && !navigator.onLine
      ? 'network-error'
      : 'ok',
  );
  const [isRetrying, setIsRetrying] = useState(false);

  // Mutable; no necesita disparar re-render en cada error.
  const trackRef = useRef<ErrorTrack>(EMPTY_TRACK);

  const escalateIfNeeded = useCallback((kind: FetchErrorKind) => {
    const now = Date.now();
    const prev = trackRef.current;
    const inWindow = prev.firstAt > 0 && now - prev.firstAt <= ERROR_WINDOW_MS;
    const next: ErrorTrack = {
      count: inWindow ? prev.count + 1 : 1,
      firstAt: inWindow ? prev.firstAt : now,
      lastKind: kind,
    };
    trackRef.current = next;

    if (next.count >= ERROR_THRESHOLD) {
      setStatus(kind === 'server' ? 'server-error' : 'network-error');
    }
  }, []);

  const resetToOk = useCallback(() => {
    trackRef.current = EMPTY_TRACK;
    setStatus((prev) => {
      // Si el browser sigue offline ignoramos — el `'offline'` listener
      // ya nos puso 'network-error' y no lo bajamos hasta que vuelva.
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        return 'network-error';
      }
      return prev === 'ok' ? prev : 'ok';
    });
  }, []);

  // Browser-level offline / online — fuente directa de verdad para
  // network. No necesitamos threshold acá; si el navegador dice offline,
  // está offline.
  useEffect(() => {
    const handleOffline = () => {
      trackRef.current = EMPTY_TRACK;
      setStatus('network-error');
    };
    const handleOnline = () => {
      // Cuando vuelve la conexión damos por OK; cualquier petición
      // siguiente que falle volverá a escalar el banner.
      resetToOk();
    };
    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, [resetToOk]);

  // Suscripción al apiClient — emite 'network'/'server' por cada error
  // de transporte/5xx, y dispara on-success cada vez que un fetch
  // termina 2xx (eso baja el banner automáticamente).
  useEffect(() => {
    const offError = apiClient.onFetchError(escalateIfNeeded);
    const offSuccess = apiClient.onFetchSuccess(resetToOk);
    return () => {
      offError();
      offSuccess();
    };
  }, [escalateIfNeeded, resetToOk]);

  const retry = useCallback(async () => {
    setIsRetrying(true);
    try {
      // Cualquier endpoint público sirve; /health es el más liviano.
      // Si responde, el `onFetchSuccess` ya bajó el banner. Si vuelve a
      // fallar, el `onFetchError` mantiene el estado.
      await apiClient.get('/health');
    } catch {
      // Mantenemos el estado actual; el listener de error ya escaló.
    } finally {
      setIsRetrying(false);
    }
  }, []);

  const value = useMemo<BackendStatusContextValue>(
    () => ({ status, retry, isRetrying }),
    [status, retry, isRetrying],
  );

  return (
    <BackendStatusContext.Provider value={value}>
      {children}
    </BackendStatusContext.Provider>
  );
}

/**
 * Hook para consumir el status global. Si se llama fuera del provider
 * (ej. en un test sin wrap, o por accidente en un módulo aislado),
 * retorna 'ok' como fallback no-disruptivo — la UI se comporta igual
 * que antes de este sistema.
 */
export function useBackendStatus(): BackendStatusContextValue {
  const ctx = useContext(BackendStatusContext);
  if (!ctx) {
    return {
      status: 'ok',
      retry: async () => undefined,
      isRetrying: false,
    };
  }
  return ctx;
}
